import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useApi from '../../helpers/TerserahApi';
import { doLogin } from '../../helpers/AuthHandler';
import { PageArea } from './style';
import { PageContainer, PageTitle, ErrorMessage } from '../../components';
import { useTranslation } from 'react-i18next';

const Page = () => {
  const api = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState(''); // Email or Phone
  const [password, setPassword] = useState('');
  const [rememberLogin, setRememberLogin] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);

  useEffect(() => {
    // Check for error in URL params
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    if (errorParam === 'auth_failed') {
      setError('Google authentication failed. Please try again.');
    }
  }, [location]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (identifier && password) {
      setDisabled(true);
      try {
        const json = await api.login(identifier, password);

        if (json.requires2FA) {
          // Redirect to 2FA verification page
          navigate('/verify-2fa', { state: { identifier: json.identifier || identifier } });
          return;
        }

        if (json.error) {
          // Check if error is about phone verification
          if (json.requirePhoneVerification || (json.error.phone && json.error.phone.msg && json.error.phone.msg.includes('Phone verification'))) {
            setShowPhoneVerificationModal(true);
            setDisabled(false);
            return;
          }
          
          // Format validation errors
          const jsonErrors = [];
          for(let err in json.error){
            if (typeof json.error[err] === 'object' && json.error[err].msg) {
              jsonErrors.push(json.error[err].msg);
            } else if (typeof json.error[err] === 'string') {
              jsonErrors.push(json.error[err]);
            }
          };
          setError(jsonErrors.join(', ') || json.message || t('login_error'));
        } else {
          doLogin(
            json.token ?? json.accessToken,
            rememberLogin,
            json.refreshToken ?? json.refresh_token
          );
          window.location.href = '/';
          return;
        };
      } catch (err) {
        setError(err.message || err.data?.message || t('login_error'));
      }
    } else {
      setError(t('fill_all_required_fields'))
    };

    setDisabled(false);
  };

  const handleGoogleLogin = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    // Ensure /api prefix is present
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    window.location.href = `${baseUrl}/auth/google`;
  };

  const handleAppleLogin = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    window.location.href = `${baseUrl}/auth/apple`;
  };

  return (
    <PageContainer>
      <PageTitle>{t('login')}</PageTitle>
      <PageArea>
        {showPhoneVerificationModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}>
              <h2 style={{ marginTop: 0, color: '#333', fontSize: '24px' }}>
                📱 {t('phone_verification_required')}
              </h2>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
                {t('phone_verification_required_message')}
              </p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => navigate('/verify-phone', { state: { email: identifier } })}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {t('verify_phone_now')}
                </button>
                <button
                  onClick={() => setShowPhoneVerificationModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    backgroundColor: '#e2e8f0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="signin-container">
          <form onSubmit={handleSubmit}>
          <label className="area">
            <div className="area--title">{t('email_or_phone')}</div>
            <div className="area--input">
              <input
                autoFocus
                type="text"
                disabled={disabled}
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder={t('email_or_phone_placeholder')}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('password')}</div>
            <div className="area--input">
              <input
                type="password"
                disabled={disabled}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('remember_me')}</div>
            <div className="area--input">
              <input
                type="checkbox"
                disabled={disabled}
                checked={rememberLogin}
                onChange={() => setRememberLogin(!rememberLogin)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title"></div>
            <div className="area--input">
              <button disabled={disabled}>{t('sign_in')}</button>
            </div>
          </label>
          <label className="area">
            <div className="area--title"></div>
            <div className="area--input">
              <a href="/forgot-password" style={{ color: '#667eea', textDecoration: 'none', fontSize: '14px' }}>
                Forgot your password?
              </a>
            </div>
          </label>
        </form>
        
        <div className="divider"></div>
        
        <div className="oauth-signin">
          <div className="oauth-buttons">
            <button onClick={handleGoogleLogin} disabled={disabled} className="oauth-btn google-btn">
              <svg viewBox="0 0 24 24" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
                <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                  <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                  <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                  <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                  <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                </g>
              </svg>
              {t('sign_in_with_google')}
            </button>
            
            <button onClick={handleAppleLogin} disabled={disabled} className="oauth-btn apple-btn">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Sign in with Apple
            </button>
          </div>
        </div>
        </div>
      </PageArea>
    </PageContainer>
  );
};

export default Page;